import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHqAccess } from './settings/hqAccess';
import {
  getOwnerNotes, createOwnerNote, updateOwnerNote, deleteOwnerNote,
  OwnerNote, setTokenProvider
} from '../api/client';

const CATEGORIES = ['general', 'employee', 'strategy', 'decision', 'relationship', 'observation'];

export function OwnerNotesPage() {
  const { user, getToken } = useAuth();
  const [notes, setNotes] = useState<OwnerNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState('');

  // BUILD-039: the Owner-only notes permission, as the server resolves it (the server checks it again on every request)
  const hq = useHqAccess();
  const isOwner = hq.can('hq.notes.use');
  void user;

  useEffect(() => {
    setTokenProvider(getToken);
    if (hq.loading) return;
    if (isOwner) loadNotes();
    else setLoading(false);
  }, [hq.loading, isOwner]);

  async function loadNotes() {
    setLoading(true);
    try {
      const data = await getOwnerNotes();
      setNotes(data.notes);
    } catch (err: any) {
      console.error('Failed to load notes:', err);
    } finally {
      setLoading(false);
    }
  }

  if (!isOwner) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-500">Owner access required.</p>
      </div>
    );
  }

  const filteredNotes = filterCategory
    ? notes.filter((n) => n.category === filterCategory)
    : notes;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Private Notes</h1>
            <p className="text-xs text-gray-500 mt-1">Only visible to you. Integrated into your AI context.</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-[#4AABDE] text-white text-sm font-medium rounded-md hover:bg-[#3a9bce]"
          >
            + New Note
          </button>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            onClick={() => setFilterCategory('')}
            className={`px-3 py-1 text-xs rounded-full ${!filterCategory ? 'bg-[#1a2332] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All ({notes.length})
          </button>
          {CATEGORIES.map((cat) => {
            const count = notes.filter((n) => n.category === cat).length;
            if (count === 0) return null;
            return (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-3 py-1 text-xs rounded-full capitalize ${filterCategory === cat ? 'bg-[#1a2332] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        {/* Create Form */}
        {showCreate && (
          <NoteForm
            onSave={async (data) => {
              await createOwnerNote(data);
              setShowCreate(false);
              loadNotes();
            }}
            onCancel={() => setShowCreate(false)}
          />
        )}

        {/* Notes List */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : filteredNotes.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-sm">
              {notes.length === 0 ? 'No private notes yet. Add observations, decisions, or strategy notes.' : 'No notes in this category.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotes.map((note) => (
              editingKey === note.key ? (
                <NoteForm
                  key={note.key}
                  initial={note}
                  onSave={async (data) => {
                    await updateOwnerNote(note.key, data);
                    setEditingKey(null);
                    loadNotes();
                  }}
                  onCancel={() => setEditingKey(null)}
                />
              ) : (
                <NoteCard
                  key={note.key}
                  note={note}
                  onEdit={() => setEditingKey(note.key)}
                  onDelete={async () => {
                    if (confirm('Delete this note?')) {
                      await deleteOwnerNote(note.key);
                      loadNotes();
                    }
                  }}
                />
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NoteCard({ note, onEdit, onDelete }: { note: OwnerNote; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900 text-sm">{note.title}</h3>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full capitalize">{note.category}</span>
          </div>
          <p className="text-gray-600 text-sm mt-2 whitespace-pre-wrap">{note.content}</p>
          <p className="text-xs text-gray-400 mt-2">
            {new Date(note.updatedAt || note.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-1 ml-3 flex-shrink-0">
          <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-[#4AABDE] hover:bg-gray-50 rounded" title="Edit">
            &#9998;
          </button>
          <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" title="Delete">
            &#10005;
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteForm({ initial, onSave, onCancel }: {
  initial?: OwnerNote;
  onSave: (data: { title?: string; content: string; category?: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title || '');
  const [content, setContent] = useState(initial?.content || '');
  const [category, setCategory] = useState(initial?.category || 'general');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim()) return;
    setSaving(true);
    try {
      await onSave({ title: title.trim() || undefined, content: content.trim(), category });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-[#4AABDE] p-4 mb-3 space-y-3">
      <input
        type="text"
        placeholder="Title (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4AABDE]"
      />
      <textarea
        placeholder="Note content *"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={4}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4AABDE]"
        autoFocus
      />
      <div className="flex items-center gap-3">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4AABDE]"
        >
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat} className="capitalize">{cat}</option>
          ))}
        </select>
        <div className="flex-1" />
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
        <button
          type="submit"
          disabled={!content.trim() || saving}
          className="px-4 py-2 bg-[#4AABDE] text-white text-sm font-medium rounded-md hover:bg-[#3a9bce] disabled:opacity-50"
        >
          {saving ? 'Saving...' : initial ? 'Update' : 'Save'}
        </button>
      </div>
    </form>
  );
}
